import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { ConsultantService } from '../../services/consultant/consultant.service';
import type { IConsultantService } from '../../services/consultant/consultant.service.interface';

export const CONSULTANT_CLIENTS_PAGE_SIZE = 50;

export interface ConsultantClientsFilters {
  search: string;
}

export const consultantQueryKeys = {
  all: ['consultant'] as const,
  clients: (page: number, pageSize: number, filters?: ConsultantClientsFilters) =>
    ['consultant', 'clients', page, pageSize, filters?.search ?? null] as const,
  access: (tenantId: string | null) => ['consultant', 'access', tenantId ?? 'no-tenant'] as const,
  clientCommissions: (tenantId: string, pageSize: number) =>
    ['consultant', 'clients', tenantId, 'commissions', pageSize] as const,
  partner: () => ['consultant', 'partner'] as const,
};

export function useConsultantClients(
  page: number,
  filters: ConsultantClientsFilters = { search: '' },
  pageSize = CONSULTANT_CLIENTS_PAGE_SIZE,
) {
  const consultantService = useInjection<IConsultantService>(ConsultantService);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: consultantQueryKeys.clients(page, pageSize, filters),
    queryFn: () => {
      const term = filters.search.trim();
      return consultantService.listClients({
        page,
        pageSize,
        search: term || undefined,
      });
    },
    placeholderData: keepPreviousData,
  });

  return {
    consultantClients: data?.items ?? [],
    total: data?.total ?? 0,
    pageSize,
    isLoading,
    isError,
    refetch,
  };
}

export const CLIENT_COMMISSIONS_PAGE_SIZE = 10;

/**
 * Provvigioni di un cliente. "Mostra altri" alza `pageSize` invece di accumulare
 * pagine: il dataset per-cliente e' piccolo (una fattura per rinnovo) e cosi' i
 * totali e le righe restano sempre di una sola risposta, senza rischio che si
 * disallineino fra loro.
 */
export function useConsultantClientCommissions(tenantId: string | null, pageSize: number) {
  const consultantService = useInjection<IConsultantService>(ConsultantService);

  const { data, isLoading, isError } = useQuery({
    queryKey: consultantQueryKeys.clientCommissions(tenantId ?? '', pageSize),
    queryFn: () => consultantService.getClientCommissions(tenantId ?? '', 1, pageSize),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });

  return { commissions: data, isLoading, isError };
}
